import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HelsinkiWaterfront, WaterfrontNightDefs } from './HelsinkiWaterfront';

/**
 * The tax-iceberg scene: one to five icebergs floating in Helsinki's South Harbour, each one
 * sized from real euro amounts (`calculate-tax-iceberg.ts`).
 *
 * How the drawing encodes the data:
 *  - Every iceberg's height is linear in euros, and all icebergs in one scene share the same
 *    euro-to-pixel scale, so two icebergs side by side are honestly comparable: twice the cost
 *    to employ someone means an iceberg twice as tall (and, since widths are equal, twice the area).
 *  - The visible tip is the money you actually get to spend (net pay after VAT). Everything
 *    below the waterline is what nobody sees on a payslip, stacked as strata: VAT paid when the
 *    net pay is spent, then the employee's own tax and contributions, then the employer's
 *    contributions at the very bottom.
 *
 * Interaction: every stratum and the tip is a hit area. Hovering shows a detail card that follows
 * the pointer; tapping (touch) or keyboard focus pins the card next to the section, and a tap
 * anywhere else, Escape, or blur closes it. In comparison mode the caption above each iceberg
 * carries a whole-iceberg summary the same way.
 *
 * Animation is pure CSS (a <style> block inside the SVG, keyframes prefixed per instance): the
 * waterline and ripples drift, icebergs bob, bubbles rise, the sun crawls, gulls cross the sky,
 * the SkyWheel turns, tiny people wander the quays and a boat passes now and then. It is all
 * disabled under `prefers-reduced-motion`. The markup itself is deterministic (no Math.random),
 * so server and client render byte-identical SVG.
 */

/** A layer key, or 'summary' for the whole-iceberg caption. */
type Part = string;

export interface IcebergLayer {
  /** Stable key: identifies the layer for hover state and the `details` map. */
  key: string;
  amount: number;
  label: string;
  color: string;
  /** Colour of the labels drawn on this layer; defaults to pale for dark layers. */
  textColor?: string;
}

export interface IcebergSpec {
  /** Stable key for React, and the basis of this iceberg's SVG ids. */
  id: string;
  /** Bottom-to-top. The LAST layer is the visible tip (what you keep); everything before it is
   * stacked under the waterline in order, so the first layer is the keel. */
  layers: IcebergLayer[];
  /** Short caption drawn above the iceberg, e.g. "€4,000 / month" or "Helsinki". */
  title?: string;
  /** Second, smaller caption line under the title. */
  subtitle?: string;
  /** Extra lines shown in the detail card of a layer (what the amount is made of), by layer key. */
  details?: Record<string, string[]>;
  /** Lines shown when the caption is hovered or tapped: the whole-iceberg summary. */
  summary?: string[];
  /** Overrides the scene's `formatShare` for this iceberg (an investments iceberg is not "what
   *  it costs to employ you"). */
  formatShare?: (fraction: number) => string;
}

export interface IcebergVisualProps {
  /** 1 to 5 icebergs. Anything beyond the fifth is dropped. */
  icebergs: IcebergSpec[];
  formatCurrency: (amount: number) => string;
  /** Turns a layer's share of the true employer cost (0..1) into a sentence for the detail card. */
  formatShare: (fraction: number) => string;
  /** Accessible description of the whole picture. */
  ariaLabel: string;
  /** Shown at the end of the legend, e.g. "All amounts per month". */
  unitLabel?: string;
  className?: string;
  /**
   * `thumbnail`: the same scene as a still picture for cards and the static thumbnail file: no
   * animation stylesheet, no hit areas, no labels, no legend or detail card, just a bare <svg>
   * (slice-cropped, `className` applied to it) so the container decides the framing. Optional
   * `crop` narrows the viewBox to an x range of the 1200-unit panorama.
   */
  variant?: 'page' | 'thumbnail';
  crop?: { x: number; width: number };
}

export const MAX_ICEBERGS = 5;

const PALE_TEXT = '#eaf6fb';
const DARK_TEXT = '#12324a';

/* Scene geometry (viewBox units). */
const WATERLINE = 310;
const SEA_BOTTOM = 572;
const H = SEA_BOTTOM;
const MARGIN = 36;
const MAX_TIP_H = 196;
const MAX_SUB_H = 240;
const MAX_BERG_W = 320;
const MAX_SINGLE_BERG_W = 360;
/** Floors for readability on tiny amounts (see layoutIcebergs). */
const MIN_TIP_H = 40;
const MIN_SUB_H = 64;
const MIN_BAND_H = 30;
const WAVELENGTH = 44;
/** The scene is always a full panorama of the waterfront (the skyline is drawn for ±600). */
const SCENE_W = 1200;

/** Width of the central band the icebergs are laid out in. On phones the picture is cropped to
 * exactly this band, so comparisons stay legible instead of shrinking with the whole panorama. */
function bandWidth(count: number): number {
  return Math.min(SCENE_W, Math.max(720, count * 214 + 100));
}

function n(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function mixHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const c = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${c(ar, br)}${c(ag, bg)}${c(ab, bb)}`;
}

/* ------------------------------------------------------------------------------------------ */
/* Silhouettes. Tip points: x in -0.5..0.5 of the tip width, y 0 (apex) .. 1 (waterline).       */
/* Submerged points: x in -0.5..0.5 of the full width, y 0 (waterline) .. 1 (keel). The         */
/* submerged outline starts at x = ±0.31 because the tip is 62% as wide as the underwater mass. */
/* ------------------------------------------------------------------------------------------ */

type Pt = [number, number];

const TIP_SHAPES: Pt[][] = [
  [
    [-0.06, 0.0],
    [0.04, 0.1],
    [0.02, 0.15],
    [0.16, 0.28],
    [0.12, 0.32],
    [0.3, 0.5],
    [0.27, 0.54],
    [0.42, 0.72],
    [0.5, 0.9],
    [0.5, 1.0],
    [-0.5, 1.0],
    [-0.5, 0.9],
    [-0.44, 0.78],
    [-0.47, 0.7],
    [-0.38, 0.52],
    [-0.42, 0.42],
    [-0.3, 0.3],
    [-0.22, 0.34],
    [-0.18, 0.22],
    [-0.12, 0.12],
  ],
  [
    [0.14, 0.0],
    [0.24, 0.08],
    [0.22, 0.14],
    [0.36, 0.3],
    [0.33, 0.36],
    [0.46, 0.55],
    [0.44, 0.62],
    [0.5, 0.8],
    [0.5, 1.0],
    [-0.5, 1.0],
    [-0.5, 0.84],
    [-0.46, 0.72],
    [-0.36, 0.56],
    [-0.4, 0.46],
    [-0.24, 0.4],
    [-0.28, 0.32],
    [-0.1, 0.22],
    [-0.02, 0.28],
    [0.02, 0.12],
    [0.08, 0.06],
  ],
];

const SUB_SHAPES: Pt[][] = [
  [
    [-0.31, 0.0],
    [0.31, 0.0],
    [0.37, 0.05],
    [0.46, 0.13],
    [0.42, 0.22],
    [0.5, 0.36],
    [0.47, 0.48],
    [0.5, 0.62],
    [0.44, 0.74],
    [0.4, 0.86],
    [0.3, 0.96],
    [0.14, 1.0],
    [-0.04, 0.97],
    [-0.2, 1.0],
    [-0.34, 0.94],
    [-0.46, 0.82],
    [-0.42, 0.7],
    [-0.5, 0.58],
    [-0.46, 0.42],
    [-0.5, 0.3],
    [-0.44, 0.18],
    [-0.38, 0.09],
    [-0.34, 0.04],
  ],
  [
    [-0.31, 0.0],
    [0.31, 0.0],
    [0.34, 0.06],
    [0.42, 0.1],
    [0.48, 0.24],
    [0.44, 0.34],
    [0.5, 0.5],
    [0.46, 0.66],
    [0.48, 0.8],
    [0.36, 0.92],
    [0.22, 1.0],
    [0.04, 0.96],
    [-0.12, 1.0],
    [-0.28, 0.97],
    [-0.4, 0.88],
    [-0.48, 0.72],
    [-0.44, 0.6],
    [-0.5, 0.44],
    [-0.46, 0.3],
    [-0.48, 0.16],
    [-0.4, 0.08],
    [-0.35, 0.03],
  ],
];

/** Mirror a clockwise outline left-to-right, keeping it clockwise and keeping the tip apex first. */
function mirrorTip(shape: Pt[]): Pt[] {
  const flipped = shape.map(([x, y]) => [-x, y] as Pt).reverse();
  // After reversing, the apex (index 0 originally) is now last; rotate it back to the front.
  const apex = flipped.pop() as Pt;
  return [apex, ...flipped];
}

function mirrorSub(shape: Pt[]): Pt[] {
  // Keep the two waterline points first (top-left, then top-right) so ridge splitting works.
  const body = shape
    .slice(2)
    .map(([x, y]) => [-x, y] as Pt)
    .reverse();
  return [shape[0], shape[1], ...body];
}

/** Five visually distinct silhouettes: A, B, mirrored A, mirrored B, then A again. */
function shapesFor(index: number): { tip: Pt[]; sub: Pt[] } {
  const v = index % 4;
  const base = v % 2;
  if (v < 2) return { tip: TIP_SHAPES[base], sub: SUB_SHAPES[base] };
  return { tip: mirrorTip(TIP_SHAPES[base]), sub: mirrorSub(SUB_SHAPES[base]) };
}

/* ------------------------------------------------------------------------------------------ */
/* Faceting: split each silhouette along a ridge into two halves, fan each half into triangles */
/* from an interior hub, and shade every triangle by how squarely it faces the light (which     */
/* comes from the upper left). Facets are clipped to the silhouette so overlaps never show.     */
/* ------------------------------------------------------------------------------------------ */

interface Facet {
  points: Pt[];
  /** 0 = deep shadow, 1 = full highlight. */
  light: number;
}

const LIGHT_DIR: Pt = [-0.55, -0.835];

function fan(polygon: Pt[], hub: Pt, seed: number): Facet[] {
  const out: Facet[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const cx = (hub[0] + a[0] + b[0]) / 3;
    const cy = (hub[1] + a[1] + b[1]) / 3;
    const dx = cx - hub[0];
    const dy = cy - hub[1];
    const len = Math.hypot(dx, dy) || 1;
    const facing = (dx / len) * LIGHT_DIR[0] + (dy / len) * LIGHT_DIR[1];
    // The outer edge's orientation adds a second, independent tilt so neighbouring facets that
    // point the same way from the hub still catch the light differently (less "spokes").
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const elen = Math.hypot(ex, ey) || 1;
    const edgeTilt = (ey / elen) * LIGHT_DIR[0] - (ex / elen) * LIGHT_DIR[1];
    const jitter = ((((i + 1) * 37 + seed * 11) % 17) / 16 - 0.5) * 0.26;
    out.push({ points: [hub, a, b], light: clamp(0.5 + 0.32 * facing + 0.2 * edgeTilt + jitter, 0, 1) });
  }
  return out;
}

function tipFacets(shape: Pt[], seed: number): Facet[] {
  const apex = shape[0];
  const rightBase = shape.findIndex(([, y]) => y === 1);
  const ridgeBase: Pt = [apex[0] * 0.35, 1];
  const right = [...shape.slice(0, rightBase + 1), ridgeBase];
  const left = [apex, ridgeBase, ...shape.slice(rightBase + 1)];
  return [...fan(right, [apex[0] * 0.5 + 0.17, 0.56], seed), ...fan(left, [apex[0] * 0.5 - 0.2, 0.6], seed + 3)];
}

function subFacets(shape: Pt[], seed: number): Facet[] {
  const top: Pt = [0, 0];
  let keel = 2;
  for (let i = 2; i < shape.length; i++) {
    if (shape[i][1] > shape[keel][1] || (shape[i][1] === shape[keel][1] && Math.abs(shape[i][0]) < Math.abs(shape[keel][0]))) keel = i;
  }
  const right = [top, ...shape.slice(1, keel + 1)];
  const left = [top, ...shape.slice(keel), shape[0]];
  return [...fan(right, [0.2, 0.46], seed + 5), ...fan(left, [-0.2, 0.5], seed + 8)];
}

/* ------------------------------------------------------------------------------------------ */

function toAttr(points: Pt[], cx: number, w: number, y0: number, h: number): string {
  return points.map(([fx, fy]) => `${n(cx + fx * w)},${n(y0 + fy * h)}`).join(' ');
}

/** Greedy word wrap on an estimated glyph width. Returns at most `maxLines` lines; the last
 * line is truncated with an ellipsis if the text does not fit. */
function wrapText(text: string, fontSize: number, maxWidth: number, maxLines: number): string[] {
  const charW = fontSize * 0.56;
  const maxChars = Math.max(4, Math.floor(maxWidth / charW));
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      current = word;
      if (lines.length === maxLines) break;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  if (lines.length > maxLines) lines.length = maxLines;
  const last = lines[lines.length - 1];
  if (last && last.length > maxChars) lines[lines.length - 1] = `${last.slice(0, Math.max(1, maxChars - 1))}…`;
  return lines;
}

/** A gently rippled horizontal line from `x0` to `x1`. */
function wavePath(x0: number, x1: number, y: number, amplitude: number, wavelength: number): string {
  const segs = Math.ceil((x1 - x0) / wavelength);
  let d = `M${n(x0)},${n(y)}`;
  for (let i = 0; i < segs; i++) {
    const xs = x0 + i * wavelength;
    d += ` Q${n(xs + wavelength * 0.25)},${n(y - amplitude)} ${n(xs + wavelength * 0.5)},${n(y)} Q${n(xs + wavelength * 0.75)},${n(y + amplitude)} ${n(xs + wavelength)},${n(y)}`;
  }
  return d;
}

function IceFloe({ x, y, size }: { x: number; y: number; size: number }) {
  const pts: Pt[] = [
    [-1, 0],
    [-0.45, -0.32],
    [0.55, -0.3],
    [1.05, 0.02],
    [0.5, 0.3],
    [-0.5, 0.32],
  ];
  const top = pts.map(([px, py]) => `${n(x + px * size)},${n(y + py * size * 0.55)}`).join(' ');
  const side = pts
    .slice(3)
    .concat([pts[0]])
    .map(([px, py]) => `${n(x + px * size)},${n(y + py * size * 0.55 + size * 0.14)}`)
    .join(' ');
  return (
    <g>
      <polygon points={side} fill="#b9d6e5" />
      <polygon points={top} fill="#eef7fb" />
      <polygon points={top} fill="#ffffff" opacity={0.35} />
    </g>
  );
}

/** Deterministic pseudo-random star field (a fixed-seed LCG, so SSR and client agree). */
function starField(width: number, count: number): Array<[number, number, number]> {
  let seed = 20260905;
  const next = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  const stars: Array<[number, number, number]> = [];
  for (let i = 0; i < count; i++) {
    const x = next() * width;
    const y = 8 + next() * next() * 190;
    const r = 0.5 + next() * 1.1;
    stars.push([Math.round(x * 10) / 10, Math.round(y * 10) / 10, Math.round(r * 100) / 100]);
  }
  return stars;
}

/* ------------------------------------------------------------------------------------------ */
/* Moving extras drawn in scene coordinates: gulls, passing boats.                             */
/* ------------------------------------------------------------------------------------------ */

function Gull({ p, y, s, duration, delay, reverse = false, flap }: { p: string; y: number; s: number; duration: number; delay: number; reverse?: boolean; flap: number }) {
  return (
    <g className={`${p}-fly`} style={{ animationDuration: `${duration}s`, animationDelay: `${delay}s`, animationDirection: reverse ? 'reverse' : 'normal' }}>
      <g transform={`translate(-60,${n(y)}) scale(${n(s)})`}>
        <g className={`${p}-flap`} style={{ animationDuration: `${flap}s`, animationDelay: `${-flap * 0.4}s` }}>
          <path d="M-5,0 q2.5,-3 5,0 q2.5,-3 5,0" fill="none" stroke="#5b6b78" strokeWidth={1.1} strokeLinecap="round" />
        </g>
      </g>
    </g>
  );
}

/** HSL Suomenlinna ferry, drawn around its own origin at the waterline, heading left. */
function SuomenlinnaFerry({ night }: { night?: string }) {
  return (
    <g>
      <polygon points="-24,26 -21,19 22,19 26,26" fill="#1f4f9e" />
      <rect x={-18} y={12} width={34} height={7} fill="#f7f7f3" />
      <rect x={-15} y={14} width={28} height={2} fill="#8fb4c9" />
      <rect x={-6} y={8} width={12} height={4} fill="#f7f7f3" />
      <rect x={2} y={4} width={3} height={4} fill="#1f4f9e" />
      <path d="M26,25 q10,-2 22,1" fill="none" stroke="#ffffff" strokeWidth={1.2} opacity={0.55} />
      <path d="M-24,24 q-6,-1 -12,1" fill="none" stroke="#ffffff" strokeWidth={1} opacity={0.5} />
      {night && (
        <g className={night}>
          <rect x={-15} y={14} width={28} height={2} fill="#ffe3a0" />
          <circle cx={-22} cy={17} r={1.3} fill="#ff6b6b" />
          <circle cx={24} cy={17} r={1.3} fill="#6bff8a" />
          <circle cx={3.5} cy={3} r={1.2} fill="#ffffff" />
          <circle cx={0} cy={15} r={16} fill="url(#hki-lamp)" opacity={0.6} />
        </g>
      )}
    </g>
  );
}

/** A small motorboat, heading right. */
function MotorBoat({ night }: { night?: string }) {
  return (
    <g>
      <polygon points="-12,12 -10,7 10,7 15,12" fill="#f0efe9" />
      <polygon points="-12,12 -10,9.5 15,9.5 15,12" fill="#4d545c" />
      <rect x={-4} y={3} width={8} height={4} fill="#4d545c" />
      <rect x={-3} y={4} width={6} height={1.5} fill="#8fb4c9" />
      <path d="M-12,11.5 q-8,-1 -16,2" fill="none" stroke="#ffffff" strokeWidth={1.2} opacity={0.6} />
      {night && (
        <g className={night}>
          <rect x={-3} y={4} width={6} height={1.5} fill="#ffe3a0" />
          <circle cx={13} cy={9} r={1} fill="#6bff8a" />
          <circle cx={-11} cy={9} r={1} fill="#ff6b6b" />
          <circle cx={0} cy={4} r={1} fill="#ffffff" />
          <circle cx={0} cy={7} r={12} fill="url(#hki-lamp)" opacity={0.55} />
        </g>
      )}
    </g>
  );
}

function SailBoat({ night }: { night?: string }) {
  return (
    <g>
      <polygon points="-11,0 -9,4 10,4 13,0" fill="#4d545c" />
      <line x1={0} y1={0} x2={0} y2={-32} stroke="#4d545c" strokeWidth={1} />
      <polygon points="0.6,-31 0.6,-2 12,-2" fill="#ffffff" />
      <polygon points="-0.6,-26 -0.6,-2 -9,-2" fill="#f1f1ec" />
      {night && (
        <g className={night}>
          <circle cx={0} cy={-32} r={1.1} fill="#ffffff" />
          <circle cx={0} cy={-32} r={5} fill="url(#hki-lamp)" opacity={0.7} />
        </g>
      )}
    </g>
  );
}

/* ------------------------------------------------------------------------------------------ */

interface LaidOutIceberg extends IcebergSpec {
  index: number;
  cx: number;
  bergW: number;
  tipW: number;
  /** Width of the keel silhouette: follows the depth, so a shallow keel is a small keel, not a
   *  wide pancake (see layoutIcebergs). */
  subW: number;
  tipH: number;
  subH: number;
  tipY: number;
  tip: Pt[];
  sub: Pt[];
  tipAmount: number;
  tipLayer: IcebergLayer;
  total: number;
  bands: Array<IcebergLayer & { y: number; h: number }>;
}

function layoutIcebergs(icebergs: IcebergSpec[], sceneWidth: number, band: number): LaidOutIceberg[] {
  const count = icebergs.length;
  const bandLeft = (sceneWidth - band) / 2;
  const slot = (band - 2 * MARGIN) / count;
  const bergW = Math.min(count === 1 ? MAX_SINGLE_BERG_W : MAX_BERG_W, slot * 0.84);

  const measured = icebergs.map((berg) => {
    const tipLayer: IcebergLayer = berg.layers[berg.layers.length - 1] ?? { key: 'tip', amount: 0, label: '', color: '#f6fbfd', textColor: DARK_TEXT };
    const tipAmount = Math.max(0, tipLayer.amount);
    const submerged = berg.layers.slice(0, -1);
    const subAmount = submerged.reduce((s, l) => s + Math.max(0, l.amount), 0);
    return { berg, tipLayer, tipAmount, submerged, subAmount };
  });
  const maxTip = Math.max(...measured.map((m) => m.tipAmount), 1);
  const maxSub = Math.max(...measured.map((m) => m.subAmount), 1);
  // One shared euro-to-pixel scale for the whole scene, chosen so the tallest tip and the
  // deepest keel both still fit their bands of the canvas.
  const scale = Math.min(MAX_TIP_H / maxTip, MAX_SUB_H / maxSub);

  return measured.map(({ berg, tipLayer, tipAmount, submerged, subAmount }, index) => {
    // Height normalisation: heights follow the shared euro scale, with floors so the labels stay
    // readable on a tiny salary (a EUR 43/month keel is a few euros deep; drawn to scale its two
    // bands could not hold their amounts). Widths are never altered: the keel is always the
    // broad base of the iceberg.
    const tipH = Math.max(MIN_TIP_H, tipAmount * scale);
    const subHRaw = Math.max(MIN_SUB_H, subAmount * scale);
    const tipW = bergW * 0.62;
    const subW = bergW;
    const cx = bandLeft + MARGIN + slot * (index + 0.5);
    const { tip, sub } = shapesFor(index);
    let cum = 0;
    // Strata top-to-bottom: VAT right under the surface, then employee tax, employer
    // contributions at the keel (the input array is bottom-to-top). Each band that carries money
    // gets at least MIN_BAND_H so its amount fits; the keel grows to hold them when needed.
    const ordered = submerged.slice().reverse();
    const heights = ordered.map((l) => {
      const amount = Math.max(0, l.amount);
      if (amount <= 0) return 0;
      const proportional = subAmount > 0 ? (amount / subAmount) * subHRaw : subHRaw / ordered.length;
      return Math.max(MIN_BAND_H, proportional);
    });
    const subH = Math.max(subHRaw, heights.reduce((a, b) => a + b, 0));
    const bands = ordered.map((l, i) => {
      const band = { ...l, y: WATERLINE + cum, h: heights[i]! };
      cum += heights[i]!;
      return band;
    });
    return {
      ...berg,
      index,
      cx,
      bergW,
      tipW,
      subW,
      tipH,
      subH,
      tipY: WATERLINE - tipH,
      tip,
      sub,
      tipAmount,
      tipLayer,
      total: tipAmount + subAmount,
      bands,
    };
  });
}

/* ------------------------------------------------------------------------------------------ */
/* Interaction plumbing                                                                        */
/* ------------------------------------------------------------------------------------------ */

interface Section {
  berg: number;
  part: Part;
}

interface SectionHandlers {
  onPointerEnter: (s: Section, e: React.PointerEvent<SVGElement>) => void;
  onPointerMove: (s: Section, e: React.PointerEvent<SVGElement>) => void;
  onPointerLeave: (s: Section, e: React.PointerEvent<SVGElement>) => void;
  onClick: (s: Section, e: React.MouseEvent<SVGElement>) => void;
  onFocus: (s: Section, e: React.FocusEvent<SVGElement>) => void;
  onBlur: (s: Section, e: React.FocusEvent<SVGElement>) => void;
  onKeyDown: (s: Section, e: React.KeyboardEvent<SVGElement>) => void;
}

/** Props every hit area gets: focusable, labelled, and wired to the shared handlers. */
function hitProps(section: Section, h: SectionHandlers, label: string) {
  return {
    'data-hit': '',
    tabIndex: 0,
    role: 'button' as const,
    'aria-label': label,
    style: { cursor: 'pointer', outline: 'none' } as React.CSSProperties,
    onPointerEnter: (e: React.PointerEvent<SVGElement>) => h.onPointerEnter(section, e),
    onPointerMove: (e: React.PointerEvent<SVGElement>) => h.onPointerMove(section, e),
    onPointerLeave: (e: React.PointerEvent<SVGElement>) => h.onPointerLeave(section, e),
    onClick: (e: React.MouseEvent<SVGElement>) => h.onClick(section, e),
    onFocus: (e: React.FocusEvent<SVGElement>) => h.onFocus(section, e),
    onBlur: (e: React.FocusEvent<SVGElement>) => h.onBlur(section, e),
    onKeyDown: (e: React.KeyboardEvent<SVGElement>) => h.onKeyDown(section, e),
  };
}

/* ------------------------------------------------------------------------------------------ */

interface IcebergProps {
  berg: LaidOutIceberg;
  idPrefix: string;
  formatCurrency: (amount: number) => string;
  /** True when this is the only iceberg in the scene: type can be larger. */
  single: boolean;
  /** Which of this iceberg's parts is hovered/pinned, if any. */
  activePart: Part | null;
  /** True when another iceberg is the active one: this one fades back a little. */
  dimmed: boolean;
  handlers: SectionHandlers;
  /** Thumbnail mode: no labels, no hit areas. */
  still?: boolean;
}

const Iceberg = React.memo(function Iceberg({ berg, idPrefix, formatCurrency, single, activePart, dimmed, handlers, still = false }: IcebergProps) {
  const { cx, bergW, tipW, subW, tipH, subH, tipY, tip, sub, index, tipLayer } = berg;
  const tipText = tipLayer.textColor ?? DARK_TEXT;
  const tipClip = `${idPrefix}-tip-${index}`;
  const subClip = `${idPrefix}-sub-${index}`;
  const tipPoints = toAttr(tip, cx, tipW, tipY, tipH);
  const subPoints = toAttr(sub, cx, subW, WATERLINE, subH);
  // Type scale follows the iceberg's width so five narrow icebergs stay legible but a single
  // wide one is not shouting.
  const f = clamp(bergW / MAX_BERG_W, 0.74, 1) * (single ? 1.22 : 1);
  const amountFont = 15 * f;
  const labelFont = 10.5 * f;
  const lineGap = labelFont * 1.2;

  const tipLabelInside = tipH >= 62;
  const tipAmountFont = 18 * f;
  const tipLabelLines = wrapText(tipLayer.label, labelFont, tipLabelInside ? tipW * (single ? 0.9 : 0.78) : bergW * 1.1, 2);
  const left = n(cx - bergW / 2);

  return (
    <g style={{ opacity: dimmed ? 0.62 : 1, transition: 'opacity 250ms ease' }}>
      <defs>
        <clipPath id={tipClip}>
          <polygon points={tipPoints} />
        </clipPath>
        <clipPath id={subClip}>
          <polygon points={subPoints} />
        </clipPath>
      </defs>

      {/* The whole iceberg bobs about the point where its centre line meets the waterline. */}
      <g transform={`translate(${n(cx)},${WATERLINE})`}>
        <g className={`${idPrefix}-bob`} style={{ animationDuration: `${6.5 + index * 0.9}s`, animationDelay: `${-index * 1.7}s` }}>
          <g transform={`translate(${n(-cx)},${-WATERLINE})`}>
            {/* ---- submerged mass: data strata, then crystalline facets, then the water's tint.
                 The whole block sits behind a refraction filter so the ice wobbles like it is seen
                 through water; the labels are drawn later, outside the filter, so they stay crisp. ---- */}
            <g clipPath={`url(#${subClip})`} filter={`url(#${idPrefix}-water)`}>
              {berg.bands.map((band) => (
                <rect key={band.key} x={left} y={n(band.y)} width={n(bergW)} height={n(band.h + 0.8)} fill={band.color} />
              ))}
              {subFacets(sub, index).map((facet, i) => {
                const pts = toAttr(facet.points, cx, subW, WATERLINE, subH);
                const lit = Math.max(0, facet.light - 0.5) * 0.36;
                const dark = Math.max(0, 0.5 - facet.light) * 0.34;
                return (
                  <g key={i}>
                    {lit > 0.005 && <polygon points={pts} fill="#dff3fb" opacity={lit.toFixed(3)} />}
                    {dark > 0.005 && <polygon points={pts} fill="#041a2c" opacity={dark.toFixed(3)} />}
                  </g>
                );
              })}
              {berg.bands.slice(1).map((band) => (
                <line key={band.key} x1={left} y1={n(band.y)} x2={n(cx + bergW / 2)} y2={n(band.y)} stroke="#e8f6fc" strokeOpacity={0.5} strokeWidth={1} />
              ))}
              {/* deeper water swallows the light */}
              <rect x={left} y={WATERLINE} width={n(bergW)} height={n(subH)} fill={`url(#${idPrefix}-depth)`} />
              <rect x={left} y={WATERLINE} width={n(bergW)} height={7} fill="#ffffff" opacity={0.16} />
              {/* hover / tap highlight of one stratum */}
              {berg.bands.map((band) =>
                activePart === band.key ? (
                  <g key={`${band.key}-hl`}>
                    <rect x={left} y={n(band.y)} width={n(bergW)} height={n(band.h + 0.8)} fill="#ffffff" opacity={0.16} />
                    <rect x={left} y={n(band.y + 1)} width={n(bergW)} height={n(Math.max(0, band.h - 1.5))} fill="none" stroke="#ffffff" strokeWidth={2} opacity={0.9} />
                  </g>
                ) : null,
              )}
            </g>
            <polygon points={subPoints} fill="none" stroke="#d6ecf6" strokeOpacity={0.45} strokeWidth={1.2} strokeLinejoin="round" />
            {/* ---- underwater light: caustic bands drifting across the ice, and slow marine snow ---- */}
            <g clipPath={`url(#${subClip})`} pointerEvents="none">
              {[0.08, 0.26, 0.47, 0.7].map((fy, i) => (
                <g key={i} className={`${idPrefix}-caustic`} style={{ animationDuration: `${9 + i * 3}s`, animationDelay: `${-i * 4}s` }}>
                  <path
                    d={wavePath(cx - bergW, cx + bergW, WATERLINE + fy * subH, 2.2 + i, 26 + i * 8)}
                    fill="none"
                    stroke={`url(#${idPrefix}-caustic)`}
                    strokeWidth={3 + i}
                    opacity={0.28 - i * 0.05}
                  />
                </g>
              ))}
              {[
                [-0.38, 0.2, 0.9],
                [-0.12, 0.55, 0.7],
                [0.22, 0.35, 1.0],
                [0.4, 0.75, 0.6],
                [-0.3, 0.85, 0.8],
                [0.05, 0.12, 0.6],
              ].map(([fx, fy, r], i) => (
                <circle
                  key={i}
                  className={`${idPrefix}-snow`}
                  style={{ animationDuration: `${11 + i * 2}s`, animationDelay: `${-i * 3}s` }}
                  cx={n(cx + fx * subW)}
                  cy={n(WATERLINE + fy * subH)}
                  r={r}
                  fill="#dff3fb"
                  opacity={0.35}
                />
              ))}
            </g>

            {/* ---- the visible tip: low-poly ice, snow on top ---- */}
            <g clipPath={`url(#${tipClip})`}>
              <rect x={n(cx - tipW / 2)} y={n(tipY)} width={n(tipW)} height={n(tipH + 1)} fill={tipLayer.color} />
              {tipFacets(tip, index).map((facet, i) => (
                <polygon key={i} points={toAttr(facet.points, cx, tipW, tipY, tipH)} fill={mixHex('#9fc4da', '#ffffff', facet.light)} />
              ))}
              <rect x={n(cx - tipW / 2)} y={n(WATERLINE - 6)} width={n(tipW)} height={7} fill="#b9dcec" opacity={0.55} />
              <ellipse cx={n(cx + tip[0][0] * tipW)} cy={n(tipY + tipH * 0.06)} rx={n(tipW * 0.16)} ry={n(tipH * 0.06)} fill="#ffffff" opacity={0.9} />
              {/* day: the sun-facing side sweats. A wet sheen down the right facets, two rivulets,
                  and a drop that gathers on an overhang and falls now and then. */}
              <g className={`${idPrefix}-day`} pointerEvents="none">
                <polygon points={toAttr(tip.slice(0, Math.max(3, Math.floor(tip.length * 0.5))), cx, tipW, tipY, tipH)} fill={`url(#${idPrefix}-melt)`} opacity={0.35} />
                {[0.22, 0.34].map((fx, i) => (
                  <path
                    key={i}
                    d={`M${n(cx + fx * tipW - 6)},${n(tipY + tipH * (0.32 + i * 0.14))} q${n(3)},${n(tipH * 0.12)} ${n(1.5)},${n(tipH * 0.26)} q${n(-1)},${n(tipH * 0.1)} ${n(2)},${n(tipH * 0.24)}`}
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={1.1}
                    strokeLinecap="round"
                    opacity={0.7}
                  />
                ))}
              </g>
              {/* night: the ice goes blue in the dark, with moonlight catching the left facets */}
              <rect className={`${idPrefix}-night`} x={n(cx - tipW / 2)} y={n(tipY)} width={n(tipW)} height={n(tipH + 1)} fill="#123a5e" opacity={0.42} />
              <polygon className={`${idPrefix}-night`} points={toAttr(tip.slice(0, Math.max(3, Math.floor(tip.length * 0.55))), cx, tipW, tipY, tipH)} fill="#dbe9ff" opacity={0.14} />
              {activePart === tipLayer.key && <rect x={n(cx - tipW / 2)} y={n(tipY)} width={n(tipW)} height={n(tipH + 1)} fill="#3a92c2" opacity={0.12} />}
            </g>
            <polygon className={`${idPrefix}-tipoutline`} points={tipPoints} fill="none" stroke={activePart === tipLayer.key ? '#2f7fb0' : '#8ebcd3'} strokeWidth={activePart === tipLayer.key ? 2.2 : 1.2} strokeLinejoin="round" />

            {/* ---- melt drops falling from the sunlit overhangs (day only) ---- */}
            {[
              [0.3, 0.5, 0],
              [0.44, 0.72, 1],
            ].map(([fx, fy, k], i) => (
              <g key={i} className={`${idPrefix}-day`} pointerEvents="none">
                <ellipse cx={n(cx + fx * tipW)} cy={n(tipY + fy * tipH)} rx={1.6} ry={2.4} fill="#e9f7ff" opacity={0.9} />
                <ellipse
                  className={`${idPrefix}-drip`}
                  /* Each drop falls exactly from its overhang to the water beside the iceberg. */
                  style={{ animationDuration: `${6 + k * 2.5}s`, animationDelay: `${-(2 + k * 1.7 + index)}s`, ['--drop' as string]: `${n(WATERLINE - (tipY + fy * tipH))}px` }}
                  cx={n(cx + fx * tipW)}
                  cy={n(tipY + fy * tipH)}
                  rx={1.3}
                  ry={2}
                  fill="#e9f7ff"
                />
              </g>
            ))}

            {/* ---- rising bubbles ---- */}
            {[
              [0.42, 0.3, 1.6, 5.2],
              [0.47, 0.52, 1.1, 6.1],
              [-0.46, 0.4, 1.3, 4.6],
              [-0.49, 0.66, 0.9, 7],
              [0.36, 0.86, 1.2, 5.6],
            ].map(([fx, fy, r, dur], i) => (
              <circle
                key={i}
                className={`${idPrefix}-bubble`}
                style={{ animationDuration: `${dur}s`, animationDelay: `${-(i * 1.3 + index)}s` }}
                cx={n(cx + fx * bergW)}
                cy={n(WATERLINE + fy * subH)}
                r={r}
                fill="#ffffff"
                opacity={0.3}
              />
            ))}

            {/* ---- labels ---- */}
            {still ? null : tipLabelInside ? (
              <g pointerEvents="none" className={`${idPrefix}-tiptext`}>
                <text x={n(cx)} y={n(tipY + tipH * 0.66)} textAnchor="middle" fontSize={n(tipAmountFont)} fontWeight={800} fill={tipText}>
                  {formatCurrency(berg.tipAmount)}
                </text>
                {tipLabelLines.map((line, i) => (
                  <text key={i} x={n(cx)} y={n(tipY + tipH * 0.66 + lineGap * (i + 1) + 2)} textAnchor="middle" fontSize={n(labelFont)} fontWeight={600} fill={tipText} opacity={0.85}>
                    {line}
                  </text>
                ))}
              </g>
            ) : (
              /* Label above a tip too small to hold it. It floats over the skyline, so the text
                 gets a pale halo and the amount sits a clear line above the caption. */
              <g pointerEvents="none" className={`${idPrefix}-tiptext`} paintOrder="stroke" stroke="#eef7fc" strokeWidth={3} strokeLinejoin="round">
                <text x={n(cx)} y={n(tipY - 10 - tipLabelLines.length * lineGap - tipAmountFont * 0.25)} textAnchor="middle" fontSize={n(tipAmountFont)} fontWeight={800} fill="#12324a">
                  {formatCurrency(berg.tipAmount)}
                </text>
                {tipLabelLines.map((line, i) => (
                  <text key={i} x={n(cx)} y={n(tipY - 10 - (tipLabelLines.length - 1 - i) * lineGap)} textAnchor="middle" fontSize={n(labelFont)} fontWeight={600} fill="#12324a" opacity={0.9}>
                    {line}
                  </text>
                ))}
              </g>
            )}
            {still ? null : berg.bands.map((band) => {
              const lines = wrapText(band.label, labelFont, subW * 0.74, 2);
              const bandText = band.textColor ?? PALE_TEXT;
              // Digits are about 0.72em tall, so a lone amount fits in a band shorter than the font size.
              const showAmount = band.h >= amountFont * 0.9;
              const showLabel = band.h >= amountFont + lineGap * lines.length + 10;
              if (!showAmount) return null;
              if (!showLabel) {
                return (
                  <text key={band.key} pointerEvents="none" x={n(cx)} y={n(band.y + band.h / 2 + amountFont * 0.36)} textAnchor="middle" fontSize={n(amountFont)} fontWeight={700} fill={bandText}>
                    {formatCurrency(band.amount)}
                  </text>
                );
              }
              const blockH = amountFont + lineGap * lines.length;
              const top = band.y + band.h / 2 - blockH / 2;
              return (
                <g key={band.key} pointerEvents="none">
                  <text x={n(cx)} y={n(top + amountFont * 0.85)} textAnchor="middle" fontSize={n(amountFont)} fontWeight={700} fill={bandText}>
                    {formatCurrency(band.amount)}
                  </text>
                  {lines.map((line, i) => (
                    <text key={i} x={n(cx)} y={n(top + amountFont + lineGap * (i + 1) - 2)} textAnchor="middle" fontSize={n(labelFont)} fontWeight={500} fill={bandText} opacity={0.85}>
                      {line}
                    </text>
                  ))}
                </g>
              );
            })}

            {/* ---- hit areas (invisible, on top of everything) ---- */}
            {!still && (
            <g clipPath={`url(#${subClip})`}>
              {/* Thin strata get a 6-unit grab zone; drawing them last keeps that zone on top of
                  the thicker neighbours it overlaps, so every band can be hovered. */}
              {berg.bands
                .slice()
                .sort((a, b) => b.h - a.h)
                .map((band) => (
                <rect
                  key={band.key}
                  x={left}
                  y={n(band.h >= 6 ? band.y : band.y + band.h / 2 - 3)}
                  width={n(bergW)}
                  height={n(Math.max(6, band.h + 0.8))}
                  fill="#000"
                  fillOpacity={0}
                  pointerEvents="all"
                  {...hitProps({ berg: index, part: band.key }, handlers, `${band.label}: ${formatCurrency(band.amount)}`)}
                />
              ))}
            </g>
            )}
            {!still && (
            <polygon
              points={tipPoints}
              fill="#000"
              fillOpacity={0}
              pointerEvents="all"
              {...hitProps({ berg: index, part: tipLayer.key }, handlers, `${tipLayer.label}: ${formatCurrency(berg.tipAmount)}`)}
            />
            )}
          </g>
        </g>
      </g>

      {/* ---- waterline: foam and the refraction glint stay on the surface while the ice bobs ---- */}
      <ellipse cx={n(cx)} cy={WATERLINE + 1} rx={n(bergW * 0.34)} ry={4.5} fill="#ffffff" opacity={0.28} />
      <line x1={n(cx - tipW / 2 - 6)} y1={WATERLINE} x2={n(cx + tipW / 2 + 6)} y2={WATERLINE} stroke="#ffffff" strokeWidth={1.6} opacity={0.85} strokeLinecap="round" />
    </g>
  );
});

function IcebergCaption({ berg, active, handlers }: { berg: LaidOutIceberg; active: boolean; handlers: SectionHandlers }) {
  if (!berg.title) return null;
  const f = clamp(berg.bergW / MAX_BERG_W, 0.74, 1);
  const titleFont = 13 * f;
  const subFont = 10.5 * f;
  // Sits above whichever is higher: the apex, or the amount label when the tip is too short
  // to hold it and the label has moved above the apex.
  const labelStackAbove = berg.tipH < 62 ? 8 + 18 * f + 2 * subFont * 1.2 + 4 : 0;
  const baseY = berg.tipY - labelStackAbove - 10;
  const pillW = Math.max(berg.title.length * titleFont * 0.6, (berg.subtitle?.length ?? 0) * subFont * 0.58) + 20;
  const pillH = berg.subtitle ? titleFont + subFont + 14 : titleFont + 10;
  const interactive = !!berg.summary?.length;
  return (
    <g {...(interactive ? hitProps({ berg: berg.index, part: 'summary' }, handlers, berg.title) : {})}>
      <rect
        x={n(berg.cx - pillW / 2)}
        y={n(baseY - pillH)}
        width={n(pillW)}
        height={n(pillH)}
        rx={n(pillH / 2)}
        fill="#ffffff"
        opacity={active ? 1 : 0.88}
        stroke={active ? '#2f7fb0' : '#9fc4da'}
        strokeWidth={active ? 1.8 : 0.8}
      />
      <text x={n(berg.cx)} y={n(baseY - pillH + titleFont + 4)} textAnchor="middle" fontSize={n(titleFont)} fontWeight={800} fill="#12324a" pointerEvents="none">
        {berg.title}
      </text>
      {berg.subtitle && (
        <text x={n(berg.cx)} y={n(baseY - 5)} textAnchor="middle" fontSize={n(subFont)} fontWeight={600} fill="#3f6a85" pointerEvents="none">
          {berg.subtitle}
        </text>
      )}
    </g>
  );
}

function Legend({ icebergs, unitLabel, handlers, active }: { icebergs: LaidOutIceberg[]; unitLabel?: string; handlers: SectionHandlers; active: Section | null }) {
  // Every distinct stratum across all icebergs, top to bottom the way they are stacked (tip
  // first, keel last), each pointing at the first iceberg that has it. Legend items are hit
  // areas: the reliable way to reach strata too thin to hover.
  const entries: Array<{ layer: IcebergLayer; berg: number }> = [];
  for (const berg of icebergs) {
    for (const layer of berg.layers.slice().reverse()) {
      if (!entries.some((e) => e.layer.key === layer.key)) entries.push({ layer, berg: berg.index });
    }
  }
  return (
    <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs font-semibold text-jc-muted">
      {entries.map(({ layer, berg }, i) => {
        const section: Section = { berg, part: layer.key };
        const isActive = !!active && active.part === layer.key;
        return (
        <li key={layer.key}>
          <button
            type="button"
            data-hit=""
            aria-pressed={isActive}
            className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-jc-elevated ${isActive ? 'bg-jc-elevated text-jc-ink' : ''}`}
            onPointerEnter={(e) => handlers.onPointerEnter(section, e as unknown as React.PointerEvent<SVGElement>)}
            onPointerMove={(e) => handlers.onPointerMove(section, e as unknown as React.PointerEvent<SVGElement>)}
            onPointerLeave={(e) => handlers.onPointerLeave(section, e as unknown as React.PointerEvent<SVGElement>)}
            onClick={(e) => handlers.onClick(section, e as unknown as React.MouseEvent<SVGElement>)}
            onFocus={(e) => handlers.onFocus(section, e as unknown as React.FocusEvent<SVGElement>)}
            onBlur={(e) => handlers.onBlur(section, e as unknown as React.FocusEvent<SVGElement>)}
            onKeyDown={(e) => handlers.onKeyDown(section, e as unknown as React.KeyboardEvent<SVGElement>)}
          >
            <span className="inline-block h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: layer.color, boxShadow: i === 0 ? 'inset 0 0 0 1px #8ebcd3' : undefined }} />
            {layer.label}
          </button>
        </li>
        );
      })}
      {unitLabel && <li className="px-1.5 font-medium italic text-jc-muted/80">{unitLabel}</li>}
    </ul>
  );
}

/** The per-instance stylesheet: keyframes and the classes that use them. */
function sceneCss(p: string, W: number): string {
  return `
@keyframes ${p}-wave { to { transform: translateX(-${WAVELENGTH}px); } }
@keyframes ${p}-drift { to { stroke-dashoffset: -${WAVELENGTH}px; } }
@keyframes ${p}-bob { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-3px) rotate(0.45deg); } }
@keyframes ${p}-bubble { 0% { transform: translateY(0); opacity: 0; } 15% { opacity: 0.35; } 100% { transform: translateY(-34px); opacity: 0; } }
@keyframes ${p}-sun { from { transform: translate(-40px, 12px); } to { transform: translate(40px, -10px); } }
@keyframes ${p}-fly { from { transform: translateX(0); } to { transform: translateX(${W + 130}px); } }
@keyframes ${p}-flap { from { transform: scaleY(1); } to { transform: scaleY(0.2); } }
@keyframes ${p}-spin { to { transform: rotate(360deg); } }
@keyframes ${p}-walk { to { transform: translateX(var(--walk, 40px)); } }
@keyframes ${p}-pass-left { 0% { transform: translateX(0); } 32% { transform: translateX(-${W + 340}px); } 100% { transform: translateX(-${W + 340}px); } }
@keyframes ${p}-pass-right { 0% { transform: translateX(0); } 26% { transform: translateX(${W + 340}px); } 100% { transform: translateX(${W + 340}px); } }
@keyframes ${p}-rock { from { transform: rotate(-3deg); } to { transform: rotate(3deg); } }
@keyframes ${p}-rock-soft { from { transform: rotate(-1.2deg) translateY(0); } to { transform: rotate(1.2deg) translateY(-1px); } }
@keyframes ${p}-sway { from { transform: translateX(-18px); } to { transform: translateX(18px); } }
@keyframes ${p}-twinkle { from { opacity: 0.25; } to { opacity: 1; } }
@keyframes ${p}-caustic { 0% { transform: translateX(-26px); opacity: 0.55; } 50% { opacity: 1; } 100% { transform: translateX(26px); opacity: 0.55; } }
@keyframes ${p}-snow { 0% { transform: translate(0, 0); opacity: 0; } 20% { opacity: 0.35; } 80% { opacity: 0.35; } 100% { transform: translate(-6px, -40px); opacity: 0; } }
@keyframes ${p}-drip { 0%, 70% { transform: translateY(0); opacity: 0; } 74% { opacity: 0.9; } 97% { opacity: 0.9; } 100% { transform: translateY(var(--drop, 40px)); opacity: 0; } }
.${p}-wave { animation: ${p}-wave 7s linear infinite; }
.${p}-ripple { animation: ${p}-drift 12s linear infinite; }
.${p}-bob { animation: ${p}-bob 7s ease-in-out infinite; }
.${p}-bubble { animation: ${p}-bubble 6s linear infinite; }
.${p}-sun { animation: ${p}-sun 240s ease-in-out infinite alternate; }
.${p}-fly { animation: ${p}-fly 60s linear infinite; }
.${p}-flap { animation: ${p}-flap 0.7s ease-in-out infinite alternate; }
.${p}-spin { animation: ${p}-spin 48s linear infinite; }
.${p}-walk { animation: ${p}-walk 30s ease-in-out infinite alternate; }
.${p}-pass-left { animation: ${p}-pass-left 170s linear infinite; }
.${p}-pass-right { animation: ${p}-pass-right 230s linear infinite; }
.${p}-rock { animation: ${p}-rock 3.2s ease-in-out infinite alternate; }
.${p}-rock-soft { animation: ${p}-rock-soft 2.6s ease-in-out infinite alternate; }
.${p}-sway { animation: ${p}-sway 40s ease-in-out infinite alternate; }
.${p}-twinkle { animation: ${p}-twinkle 3s ease-in-out infinite alternate; }
.${p}-caustic { animation: ${p}-caustic 12s ease-in-out infinite alternate; }
.${p}-snow { animation: ${p}-snow 14s linear infinite; }
.${p}-drip { animation: ${p}-drip 7s cubic-bezier(0.4, 0, 1, 1) infinite; }
/* Night: shown only when the page is in dark mode (a .dark class on the html element). */
.${p}-night { display: none; }
.dark .${p}-night { display: inline; }
.dark .${p}-day { display: none; }
.dark .${p}-sky { fill: url(#${p}-sky-night); }
.dark .${p}-sea { fill: url(#${p}-sea-night); }
.dark .${p}-city { filter: brightness(0.42) saturate(0.7); }
.dark .${p}-city .${p}-night { filter: brightness(2.4) saturate(1.4); }
.dark .${p}-city .hki-win:nth-of-type(3n+1), .dark .${p}-city .hki-win:nth-of-type(7n+2) { fill: #ffd98a; }
.dark .${p}-city .hki-glass:nth-of-type(2n) { fill: #ffe3a0; }
.dark .${p}-city .hki-arch:nth-of-type(2n+1) { fill: #f4c26a; }
.dark .${p}-tiptext text { fill: #eaf6fb; }
.dark .${p}-tipoutline { stroke: #7fb0d6; }
@media (prefers-reduced-motion: reduce) { .${p}-scene * { animation: none !important; } }
`;
}

/* ------------------------------------------------------------------------------------------ */

interface ActiveState {
  section: Section;
  /** Pinned by tap/click/keyboard: survives pointer leave, closes on outside tap or Escape. */
  pinned: boolean;
  /** Anchor the card to the element (tap/keyboard) instead of following the pointer. */
  anchored: boolean;
}

export function IcebergVisual({ icebergs, formatCurrency, formatShare, ariaLabel, unitLabel, className, variant = 'page', crop }: IcebergVisualProps) {
  const still = variant === 'thumbnail';
  // React 19's useId contains characters that are awkward inside url(#…) references; keep the
  // unique digits and drop the rest.
  const idPrefix = `berg${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const specs = icebergs.slice(0, MAX_ICEBERGS);
  const count = Math.max(1, specs.length);
  const W = SCENE_W;
  const CX = W / 2;
  const band = bandWidth(count);
  const laidOut = layoutIcebergs(specs, W, band);
  const skylineId = `${idPrefix}-hki`;
  const single = count === 1;
  // Below `lg` the picture is cropped to the iceberg band (see the wrapper comment), and below
  // `sm` a single iceberg gets a portrait 4:5 window instead. The band scrolls horizontally at a
  // minimum width so five icebergs are never squeezed into a phone.
  const bandAspect = `${band} / ${H}`;
  const phoneAspect = single ? '4 / 5' : bandAspect;
  const mobileMinWidth = single ? undefined : Math.max(640, Math.round(band * 0.8));

  const wrapperRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const [active, setActive] = useState<ActiveState | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  /** Position the card near a point given in wrapper-relative CSS pixels, kept inside the wrapper. */
  const place = useCallback((x: number, y: number) => {
    const wrapper = wrapperRef.current;
    const tip = tipRef.current;
    if (!wrapper || !tip) return;
    lastPoint.current = { x, y };
    const ww = wrapper.clientWidth;
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const left = clamp(x - tw / 2, 4, Math.max(4, ww - tw - 4));
    const top = y - th - 14 >= 0 ? y - th - 14 : y + 22;
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  }, []);

  const handlers = useMemo<SectionHandlers>(() => {
    const pointFromEvent = (e: { clientX: number; clientY: number }) => {
      const r = wrapperRef.current?.getBoundingClientRect();
      return r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: 0, y: 0 };
    };
    const pointFromElement = (el: Element) => {
      const r = wrapperRef.current?.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return r ? { x: b.left + b.width / 2 - r.left, y: b.top - r.top + Math.min(b.height * 0.35, 40) } : { x: 0, y: 0 };
    };
    const same = (a: Section, b: Section) => a.berg === b.berg && a.part === b.part;
    const pin = (section: Section, el: Element) => {
      lastPoint.current = pointFromElement(el);
      setActive({ section, pinned: true, anchored: true });
    };
    return {
      onPointerEnter: (section, e) => {
        if (e.pointerType !== 'mouse' || activeRef.current?.pinned) return;
        lastPoint.current = pointFromEvent(e);
        setActive({ section, pinned: false, anchored: false });
      },
      onPointerMove: (section, e) => {
        if (e.pointerType !== 'mouse') return;
        const cur = activeRef.current;
        if (!cur || cur.pinned || cur.anchored || !same(cur.section, section)) return;
        const p = pointFromEvent(e);
        place(p.x, p.y);
      },
      onPointerLeave: (section, e) => {
        if (e.pointerType !== 'mouse') return;
        const cur = activeRef.current;
        if (cur && !cur.pinned && same(cur.section, section)) setActive(null);
      },
      onClick: (section, e) => {
        const cur = activeRef.current;
        if (cur?.pinned && same(cur.section, section)) setActive(null);
        else pin(section, e.currentTarget);
      },
      onFocus: (section, e) => {
        lastPoint.current = pointFromElement(e.currentTarget);
        setActive({ section, pinned: false, anchored: true });
      },
      onBlur: (section) => {
        const cur = activeRef.current;
        if (cur && !cur.pinned && same(cur.section, section)) setActive(null);
      },
      onKeyDown: (section, e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const cur = activeRef.current;
          if (cur?.pinned && same(cur.section, section)) setActive(null);
          else pin(section, e.currentTarget);
        } else if (e.key === 'Escape') {
          setActive(null);
        }
      },
    };
  }, [place]);

  // A pinned card closes on a tap/click anywhere that is not a hit area, and on Escape.
  useEffect(() => {
    if (!active?.pinned) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.('[data-hit]')) return;
      setActive(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [active?.pinned]);

  // Re-place after the card's content (and therefore size) changes.
  useLayoutEffect(() => {
    if (active && lastPoint.current) place(lastPoint.current.x, lastPoint.current.y);
  }, [active, place]);

  const activeBerg = active ? laidOut[active.section.berg] : null;
  const card = (() => {
    if (!active || !activeBerg) return null;
    const part = active.section.part;
    if (part === 'summary') {
      return { title: activeBerg.title ?? '', amount: null as string | null, share: null as string | null, lines: activeBerg.summary ?? [] };
    }
    const layer = activeBerg.layers.find((l) => l.key === part);
    const amount = Math.max(0, layer?.amount ?? 0);
    return {
      title: layer?.label ?? '',
      amount: formatCurrency(amount),
      share: activeBerg.total > 0 ? (activeBerg.formatShare ?? formatShare)(amount / activeBerg.total) : null,
      lines: activeBerg.details?.[part] ?? [],
    };
  })();

  const scene = (
            <svg
              viewBox={crop ? `${n(crop.x)} 0 ${n(crop.width)} ${H}` : `0 0 ${W} ${H}`}
              preserveAspectRatio="xMidYMid slice"
              className={still ? (className ?? 'block h-full w-full') : `${idPrefix}-scene block h-full w-full lg:h-auto`}
              role="img"
              aria-label={ariaLabel}
              aria-hidden={still ? true : undefined}
            >
              <title>{ariaLabel}</title>
              <defs>
                {/* Stills carry no animation stylesheet, only the rule that keeps the night layer off. */}
                <style>{still ? `.${idPrefix}-night { display: none; }` : sceneCss(idPrefix, W)}</style>
                <linearGradient id={`${idPrefix}-sky`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#9cc6e2" />
                  <stop offset="55%" stopColor="#d3e7f1" />
                  <stop offset="100%" stopColor="#f3e8d8" />
                </linearGradient>
                <radialGradient id={`${idPrefix}-sunglow`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#fff6d6" stopOpacity={0.95} />
                  <stop offset="45%" stopColor="#ffe9b8" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#ffe9b8" stopOpacity={0} />
                </radialGradient>
                <linearGradient id={`${idPrefix}-sea`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a9d4e7" />
                  <stop offset="10%" stopColor="#5f9fc4" />
                  <stop offset="45%" stopColor="#1f5d86" />
                  <stop offset="100%" stopColor="#0a2b45" />
                </linearGradient>
                <linearGradient id={`${idPrefix}-depth`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0a2d48" stopOpacity={0} />
                  <stop offset="100%" stopColor="#051d33" stopOpacity={0.5} />
                </linearGradient>
                <linearGradient id={`${idPrefix}-refl-grad`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity={1} />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id={`${idPrefix}-sky-night`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#060f22" />
                  <stop offset="60%" stopColor="#12294a" />
                  <stop offset="100%" stopColor="#2b4c6e" />
                </linearGradient>
                <linearGradient id={`${idPrefix}-sea-night`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2a4c6d" />
                  <stop offset="12%" stopColor="#173a5a" />
                  <stop offset="50%" stopColor="#0b2340" />
                  <stop offset="100%" stopColor="#040f1f" />
                </linearGradient>
                <radialGradient id={`${idPrefix}-moonglow`} cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#e8f1ff" stopOpacity={0.6} />
                  <stop offset="35%" stopColor="#cfe0f7" stopOpacity={0.22} />
                  <stop offset="100%" stopColor="#cfe0f7" stopOpacity={0} />
                </radialGradient>
                <linearGradient id={`${idPrefix}-moonpath`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#dbe9ff" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#dbe9ff" stopOpacity={0} />
                </linearGradient>
                <WaterfrontNightDefs />
                {/* refraction wobble for everything under the waterline: static turbulence, small scale */}
                <filter id={`${idPrefix}-water`} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
                  <feTurbulence type="fractalNoise" baseFrequency="0.012 0.03" numOctaves={2} seed={7} result="noise" />
                  <feDisplacementMap in="SourceGraphic" in2="noise" scale={5} xChannelSelector="R" yChannelSelector="G" />
                </filter>
                <linearGradient id={`${idPrefix}-caustic`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity={0} />
                  <stop offset="50%" stopColor="#dff6ff" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
                </linearGradient>
                <linearGradient id={`${idPrefix}-melt`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#bfe4f4" stopOpacity={0} />
                </linearGradient>
                <mask id={`${idPrefix}-refl`} maskUnits="userSpaceOnUse" x="0" y={WATERLINE} width={W} height={110}>
                  <rect x="0" y={WATERLINE} width={W} height={110} fill={`url(#${idPrefix}-refl-grad)`} />
                </mask>
              </defs>

              {/* ---- sky ---- */}
              <rect className={`${idPrefix}-sky`} x={0} y={0} width={W} height={WATERLINE} fill={`url(#${idPrefix}-sky)`} />
              {/* night sky: stars twinkle, a full moon drifts where the sun does by day */}
              <g className={`${idPrefix}-night`}>
                {starField(W, 90).map(([x, y, r], i) => (
                  <circle key={i} className={`${idPrefix}-twinkle`} style={{ animationDuration: `${2.4 + (i % 7) * 0.6}s`, animationDelay: `${-(i % 11) * 0.5}s` }} cx={x} cy={y} r={r} fill="#ffffff" />
                ))}
                <g className={`${idPrefix}-sun`}>
                  <circle cx={n(W * 0.74)} cy={112} r={90} fill={`url(#${idPrefix}-moonglow)`} />
                  <circle cx={n(W * 0.74)} cy={112} r={20} fill="#f2f6ff" />
                  <circle cx={n(W * 0.74 - 6)} cy={106} r={4} fill="#d9e3f4" opacity={0.8} />
                  <circle cx={n(W * 0.74 + 7)} cy={116} r={2.6} fill="#d9e3f4" opacity={0.8} />
                  <circle cx={n(W * 0.74 - 2)} cy={121} r={2} fill="#d9e3f4" opacity={0.7} />
                </g>
              </g>
              <g className={`${idPrefix}-sun ${idPrefix}-day`}>
                <circle cx={n(W * 0.74)} cy={118} r={78} fill={`url(#${idPrefix}-sunglow)`} />
                <circle cx={n(W * 0.74)} cy={118} r={21} fill="#fff4d2" />
              </g>
              {[
                [0.16, 62, 1],
                [0.5, 44, 0.8],
                [0.9, 84, 0.7],
                [0.32, 98, 0.55],
              ].map(([fx, y, s], i) => (
                <g key={i} opacity={0.82} className={`${idPrefix}-sway`} style={{ animationDuration: `${50 + i * 14}s`, animationDelay: `${-i * 9}s` }}>
                  <ellipse cx={n(W * fx)} cy={y} rx={n(46 * s)} ry={n(7 * s)} fill="#ffffff" />
                  <ellipse cx={n(W * fx - 22 * s)} cy={n(y + 3 * s)} rx={n(26 * s)} ry={n(5.5 * s)} fill="#ffffff" />
                  <ellipse cx={n(W * fx + 18 * s)} cy={n(y - 4 * s)} rx={n(22 * s)} ry={n(6 * s)} fill="#ffffff" />
                </g>
              ))}
              {/* gulls crossing the sky at their own pace; two fly the other way */}
              <Gull p={idPrefix} y={150} s={0.9} duration={64} delay={-21} flap={0.72} />
              <Gull p={idPrefix} y={128} s={0.7} duration={58} delay={-49} flap={0.6} />
              <Gull p={idPrefix} y={118} s={1} duration={82} delay={-12} flap={0.8} />
              <Gull p={idPrefix} y={165} s={0.75} duration={70} delay={-40} flap={0.66} reverse />
              <Gull p={idPrefix} y={104} s={0.6} duration={96} delay={-70} flap={0.58} reverse />

              {/* ---- Helsinki ---- */}
              <g className={`${idPrefix}-city`} transform={`translate(${n(CX)},${WATERLINE})`}>
                <HelsinkiWaterfront id={skylineId} animPrefix={idPrefix} />
              </g>

              {/* ---- sea ---- */}
              <rect className={`${idPrefix}-sea`} x={0} y={WATERLINE} width={W} height={SEA_BOTTOM - WATERLINE} fill={`url(#${idPrefix}-sea)`} />
              {/* moonlight on the water */}
              <g className={`${idPrefix}-night`}>
                <rect x={n(W * 0.74 - 70)} y={WATERLINE} width={140} height={150} fill={`url(#${idPrefix}-moonpath)`} opacity={0.35} />
                {[6, 14, 24, 36, 50, 66, 84, 104, 126].map((dy, i) => (
                  <ellipse
                    key={dy}
                    className={`${idPrefix}-twinkle`}
                    style={{ animationDuration: `${1.8 + (i % 4) * 0.5}s`, animationDelay: `${-i * 0.37}s` }}
                    cx={n(W * 0.74 + ((i % 3) - 1) * 9)}
                    cy={WATERLINE + dy}
                    rx={n(10 + i * 2.5)}
                    ry={1.1}
                    fill="#e6f0ff"
                    opacity={0.5 - i * 0.04}
                  />
                ))}
              </g>
              <g mask={`url(#${idPrefix}-refl)`}>
                <g transform={`translate(${n(CX)},${WATERLINE}) scale(1,-1)`} opacity={0.2}>
                  <use href={`#${skylineId}`} />
                </g>
              </g>
              {[8, 19, 33, 52, 74].map((dy, i) => (
                <path
                  key={dy}
                  className={`${idPrefix}-ripple`}
                  style={{ animationDuration: `${9 + i * 2.5}s`, animationDirection: i % 2 ? 'reverse' : 'normal' }}
                  d={wavePath(0, W, WATERLINE + dy, 0.8, 34 + i * 6)}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={0.8}
                  opacity={0.18 - i * 0.025}
                  strokeDasharray={`${18 + i * 3} ${WAVELENGTH - 18 - i * 3}`}
                />
              ))}

              {/* ---- boats behind the ice: the ferry passes now and then, a sailboat rocks at anchor ---- */}
              <g className={`${idPrefix}-pass-left`} style={{ animationDelay: '-14s' }}>
                <g transform={`translate(${n(W + 150)},${WATERLINE})`}>
                  <SuomenlinnaFerry night={`${idPrefix}-night`} />
                </g>
              </g>
              <g className={`${idPrefix}-sway`} style={{ animationDuration: '46s', animationDelay: '-11s' }}>
                <g transform={`translate(${n(W * 0.93)},${WATERLINE + 20})`}>
                  <g className={`${idPrefix}-rock`}>
                    <SailBoat night={`${idPrefix}-night`} />
                  </g>
                </g>
              </g>

              {/* ---- icebergs ---- */}
              {laidOut.map((berg) => (
                <Iceberg
                  key={berg.id}
                  berg={berg}
                  idPrefix={idPrefix}
                  formatCurrency={formatCurrency}
                  single={single}
                  activePart={active && active.section.berg === berg.index ? active.section.part : null}
                  dimmed={!single && !!active && active.section.berg !== berg.index}
                  handlers={handlers}
                  still={still}
                />
              ))}

              {/* ---- surface: waterline, brash ice ---- */}
              <path className={`${idPrefix}-wave`} d={wavePath(-WAVELENGTH, W + WAVELENGTH, WATERLINE, 1.2, WAVELENGTH)} fill="none" stroke="#ffffff" strokeWidth={1.4} opacity={0.75} />
              {laidOut.flatMap((berg) => {
                const floes: Array<[number, number, number]> = [
                  [berg.cx - berg.bergW * 0.66, WATERLINE + 20, 11],
                  [berg.cx + berg.bergW * 0.6, WATERLINE + 11, 8],
                  [berg.cx + berg.bergW * 0.5, WATERLINE + 36, 13],
                  [berg.cx - berg.bergW * 0.52, WATERLINE + 46, 9],
                ];
                return floes
                  .filter(([x]) => x > 14 && x < W - 14)
                  .map(([x, y, size], i) => (
                    <g key={`${berg.id}-${i}`} className={`${idPrefix}-sway`} style={{ animationDuration: `${28 + i * 7 + berg.index * 3}s`, animationDelay: `${-(i * 11 + berg.index * 5)}s` }}>
                      <IceFloe x={x} y={y} size={size} />
                    </g>
                  ));
              })}
              <IceFloe x={W * 0.06} y={WATERLINE + 58} size={10} />
              <IceFloe x={W * 0.95} y={WATERLINE + 66} size={12} />

              {/* ---- foreground: a motorboat passes in front of the ice now and then. Drawn after the
                   icebergs and lower on the water, larger, with its own reflection, so the scene has
                   a near plane as well as the far one the ferry moves on. ---- */}
              <g className={`${idPrefix}-pass-right`} style={{ animationDelay: '-150s' }}>
                <g transform={`translate(-170,${WATERLINE + 74}) scale(1.7)`}>
                  <g transform="translate(0,12) scale(1,-1) translate(0,-12)" opacity={0.22}>
                    <MotorBoat />
                  </g>
                  <g className={`${idPrefix}-rock-soft`} style={{ animationDelay: '-1s' }}>
                    <MotorBoat night={`${idPrefix}-night`} />
                  </g>
                </g>
              </g>

              {/* ---- captions ---- */}
              {!still &&
                laidOut.map((berg) => (
                  <IcebergCaption key={`${berg.id}-cap`} berg={berg} active={!!active && active.section.berg === berg.index && active.section.part === 'summary'} handlers={handlers} />
                ))}
            </svg>
  );

  if (still) return scene;

  return (
    <div className={className}>
      {/*
        Responsive behaviour, all in CSS so server and client markup match:
        - The SVG is always the full 1200-unit panorama, and it uses `slice`, so whenever the box
          is narrower than the panorama's own aspect the outer edges are cropped symmetrically and
          the centre stays large. From `lg` (1024px) up the box follows the viewBox's aspect:
          nothing is cropped and the whole waterfront shows.
        - Between `sm` and `lg` (tablets, small laptops) the box's aspect equals the iceberg band
          (720 units for one to three icebergs), so the crop is the classic Cathedral-to-Uspenski
          view at close to 1:1 scale instead of a 0.6x panorama.
        - One iceberg on a phone (< 640px): a 4:5 portrait box, so the iceberg, Helsinki Cathedral
          and Uspenski stay in frame at a legible scale.
        - Two or more icebergs on a phone: the band aspect again, plus a minimum width (640px, or
          80% of the band for four and five icebergs) so the band scrolls horizontally rather than
          shrinking.
        The scene's height is never capped to the viewport on purpose: cropping vertically would
        cut the keel (and the employer-contributions label) on landscape phones.
      */}
      <div className="relative" ref={wrapperRef}>
        {/* `contain-inline-size` stops the SVG's minimum width from leaking into the page's
            min-content width: the layout's <main> is a flex item, and without this the whole
            page would silently grow to 700px on a 360px phone (the layout's overflow-x-clip
            hides the damage, but every card would be wider than the screen). */}
        <div className="contain-inline-size w-full overflow-x-auto">
          <div
            className="relative aspect-(--berg-aspect) w-full sm:aspect-(--berg-aspect-md) lg:aspect-auto"
            style={{ ['--berg-aspect' as string]: phoneAspect, ['--berg-aspect-md' as string]: bandAspect, minWidth: mobileMinWidth }}
          >
            {scene}
          </div>
        </div>
        {!single && (
          /* phones: a soft fade on the right edge hints that the comparison continues off-screen */
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-jc-surface/90 to-transparent lg:hidden" />
        )}

        {/* ---- detail card ---- */}
        <div
          ref={tipRef}
          role="status"
          aria-live="polite"
          hidden={!card}
          className="pointer-events-none absolute z-10 w-max max-w-[min(280px,calc(100%-8px))] rounded-xl border border-jc-card-border bg-jc-surface/95 px-3 py-2 text-left shadow-[0_8px_24px_rgba(22,21,15,0.18)] backdrop-blur-sm"
        >
          {card && (
            <>
              <div className="text-[11px] font-bold uppercase tracking-wide text-jc-muted">{card.title}</div>
              {card.amount && <div className="font-display text-base font-bold leading-tight text-jc-ink">{card.amount}</div>}
              {card.share && <div className="text-xs text-jc-muted">{card.share}</div>}
              {card.lines.length > 0 && (
                <ul className={`space-y-0.5 text-xs text-jc-ink ${card.amount ? 'mt-1.5 border-t border-jc-border pt-1.5' : 'mt-0.5'}`}>
                  {card.lines.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
      {/* The scene bleeds to the card's edges; only the legend keeps a bezel. */}
      <div className="px-2 pb-2 sm:px-3 sm:pb-3">
        <Legend icebergs={laidOut} unitLabel={unitLabel} handlers={handlers} active={active?.section ?? null} />
      </div>
    </div>
  );
}
